import { TrackingEvents, track } from "#webapp/src//util/analytics.util";
import { DurationPicker } from "#webapp/src/components/duration-picker";
import { useMutation } from "@apollo/client";
import { articleUrls } from "@clockwise/client-commons/src/constants/help-center";
import { paths } from "@clockwise/client-commons/src/constants/site";
import { Link } from "@clockwise/design-system";
import { DateTimeRange, EcosystemEnum } from "@clockwise/schema";
import {
  appPaths,
  webappDefaultWindowName,
} from "@clockwise/web-commons/src/constants/route.constants";
import { getFormattedEventTime } from "@clockwise/web-commons/src/util/date.util";
import { useEcosystem } from "@clockwise/web-commons/src/util/ecosystem";
import { getUrlForFeature } from "@clockwise/web-commons/src/util/routes.util";
import { useRouter } from "found";
import { Duration } from "luxon";
import React from "react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { IReduxState } from "../../../state/reducers/root.reducer";
import { ExpandableHelpText } from "./ExpandableHelpText";
import { GoalWithIcon } from "./GoalWithIcon";
import RemoveHold from "./RemoveHold.container";
import { UpdateTravelTimeDurationDocument } from "./__generated__/UpdateTravelTimeDuration.generated";

export type TravelTimeHoldProps = {
  holdId: string;
  orgId: string;
  calendarId: string;
  title: string;
  times: DateTimeRange | undefined;
};

const DEFAULT_ISO_DURATION = "PT30M";

export const TravelTimeHold = ({
  holdId,
  orgId,
  calendarId,
  title,
  times,
}: TravelTimeHoldProps) => {
  const ecosystem = useEcosystem();
  const isWebExtension = useSelector((state: IReduxState) => state.webExtension.isWebExtension);
  const routerState = useRouter();

  const [updateTravelTimeDuration] = useMutation(UpdateTravelTimeDurationDocument);

  const formattedEventTime = times
    ? getFormattedEventTime({ start: times.start, end: times.end })
    : null;
  const defaultDuration = Duration.fromISO(times?.duration ?? DEFAULT_ISO_DURATION).as("minutes");

  const onClickEditSettings = () => {
    if (ecosystem === EcosystemEnum.Microsoft) {
      window.open(
        `${paths.webApp}${paths.webSettings}/account/smart-holds/travel-time`,
        "web-settings",
      );
    } else if (isWebExtension) {
      window.open(getUrlForFeature("accountPreferences"), webappDefaultWindowName);
    } else {
      routerState?.router?.replace(appPaths.accountPreferences);
    }
  };

  const onUpdateTravelTime = (updatedDurationMinutes: number) => {
    void updateTravelTimeDuration({
      variables: { input: { orgId, holdId, durationMinutes: updatedDurationMinutes }, calendarId },
      onCompleted: (data) => {
        if (data?.updateTravelTimeDuration?.success) {
          track(TrackingEvents.EVENT.EDIT_TRAVEL_TIME_EVENT);
          toast.success(
            `This travel time event will be updated to ${updatedDurationMinutes} minutes. Please wait a few moments for the change to occur.`,
          );
        }
      },
      onError: () => {
        toast.error("Sorry, there was a problem updating your travel time hold.");
      },
    });
  };

  return (
    <div>
      <div className="cw-heading-xl">{title}</div>
      {formattedEventTime && <div className="cw-body-base cw-mb-4">{formattedEventTime}</div>}
      <div className="cw-border cw-border-solid cw-border-positive-interactable cw-rounded">
        <div className="cw-p-3 cw-bg-positive cw-rounded-t cw-border-0 cw-border-b cw-border-solid cw-border-positive-interactable">
          <div className="cw-heading-sm cw-leading-none cw-text-positive cw-flex">
            Clockwise travel time
          </div>
        </div>
        <div className="cw-p-3">
          <div className="cw-flex cw-flex-wrap cw-gap-2 cw-items-center">
            <span>Hold</span>
            <DurationPicker
              onDurationChange={onUpdateTravelTime}
              durations={[15, 30, 45, 60]}
              defaultDuration={defaultDuration}
              maxDuration={120}
              disabled={false}
              showLabel={false}
            />
            <span>of travel time</span>
          </div>
        </div>
      </div>
      <GoalWithIcon holdName="TravelTimeHold">
        <div>
          <p className="cw-body-base">
            Clockwise is holding travel time for all external meetings that have a location set ·{" "}
            <Link onClick={onClickEditSettings}>Edit settings</Link>
          </p>
        </div>
      </GoalWithIcon>
      <RemoveHold holdId={holdId} orgId={orgId} />
      <ExpandableHelpText>
        <div className="cw-body-base">
          <p>This travel time event was automatically created by Clockwise. </p>
          <p className="cw-mt-1 cw-mb-2">This event can be edited or removed. </p>
          <Link href={articleUrls.travelTime} target="_blank">
            Tell me more
          </Link>
        </div>
      </ExpandableHelpText>
    </div>
  );
};
